import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sun, Droplets, Apple, Activity, Leaf, Moon, RefreshCw, Sparkles, AlertTriangle, Wind } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card className="rounded-2xl bg-card border-border/70 p-6" data-testid={testId}>
    <h3 className="font-display text-2xl text-ink flex items-center gap-2">
      <Icon className="h-5 w-5 text-rose-gold" /> {title}
    </h3>
    <div className="mt-4">{children}</div>
  </Card>
);

const Pill = ({ children }) => (
  <span className="text-xs rounded-full border border-border/70 bg-cream-paper px-3 py-1">{children}</span>
);

export default function LifestylePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadCached = async () => {
    setLoading(true);
    try {
      const [meRes, wRes] = await Promise.all([
        api.get('/lifestyle/me'),
        api.get('/weather'),
      ]);
      setWeather(wRes.data);
      if (meRes.data?.lifestyle) setData(meRes.data.lifestyle);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) loadCached(); }, [user]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: res } = await api.get('/lifestyle/suggestions');
      setData(res.lifestyle);
      setWeather(res.weather);
      toast.success('Your lifestyle plan is ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Couldn\u2019t generate. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="lifestyle-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <Leaf className="h-3.5 w-3.5" /> Glow from within
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Foods, yoga &amp; lifestyle.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Region-aware, weather-aware suggestions for hydration, foods, dry fruits, yoga, exercise and lifestyle changes —
          tailored to where you live and how your skin behaves.
        </p>
      </div>

      {/* Weather snapshot */}
      {weather?.available && (
        <Card className="mt-6 rounded-2xl bg-cream-paper border-border/70 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-card flex items-center justify-center border border-border">
                <Sun className="h-6 w-6 text-rose-gold" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Today in {weather.city || 'your region'}
                </p>
                <p className="font-display text-xl text-ink">
                  {Math.round(weather.temp_c)}°C · {weather.humidity}% humidity · UV {Math.round(weather.uv_index)}
                </p>
                <p className="text-sm text-muted-foreground">{weather.advice}</p>
              </div>
            </div>
            <Button
              onClick={generate}
              disabled={generating}
              className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="lifestyle-generate-button"
            >
              {generating ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Crafting...</>
              ) : data ? (
                <><Sparkles className="h-4 w-4 mr-1" /> Refresh plan</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-1" /> Generate my plan</>
              )}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-2xl" />
          ))}
        </div>
      ) : !data ? (
        <Card className="mt-8 rounded-2xl bg-card border-border/70 p-10 text-center">
          <Sparkles className="h-9 w-9 mx-auto text-rose-gold" />
          <p className="mt-3 font-display text-2xl text-ink">No lifestyle plan yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Click <em className="not-italic font-medium">Generate my plan</em> above and we’ll craft a region- &amp; weather-aware
            wellness plan in seconds.
          </p>
          <div className="mt-5">
            <Button onClick={generate} disabled={generating} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
              {generating ? 'Generating...' : 'Generate my plan'}
            </Button>
          </div>
          {!user?.region && (
            <p className="mt-4 text-xs text-muted-foreground">
              Tip: set your region in <Link to="/profile" className="underline underline-offset-4">profile</Link> for the most local picks.
            </p>
          )}
        </Card>
      ) : (
        <div className="mt-8 space-y-5" data-testid="lifestyle-content">
          {data.headline && (
            <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight">{data.headline}</h2>
          )}
          {data.intro && <p className="text-muted-foreground leading-relaxed max-w-3xl">{data.intro}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.hydration && (
              <Section title="Hydration" icon={Droplets} testId="lifestyle-hydration">
                {data.hydration.daily_goal_liters && (
                  <p className="text-3xl font-display text-ink">
                    {data.hydration.daily_goal_liters} <span className="text-base text-muted-foreground">L per day</span>
                  </p>
                )}
                <ul className="mt-3 space-y-1.5">
                  {(data.hydration.tips || []).map((t, i) => (
                    <li key={i} className="text-sm flex gap-2 items-start">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-gold shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(data.foods_to_eat || []).length > 0 && (
              <Section title="Foods to enjoy" icon={Apple} testId="lifestyle-foods">
                <ul className="space-y-2">
                  {data.foods_to_eat.map((f, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium">{f.name}</span>{' '}
                      <span className="text-muted-foreground">— {f.why}</span>
                      {f.kind && <span className="text-[10px] ml-2 uppercase tracking-[0.14em] text-rose-gold">{f.kind}</span>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(data.dry_fruits_and_seeds || []).length > 0 && (
              <Section title="Dry fruits & seeds" icon={Leaf} testId="lifestyle-nuts">
                <ul className="space-y-2">
                  {data.dry_fruits_and_seeds.map((f, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium">{f.name}</span>{' '}
                      <span className="text-muted-foreground">— {f.why}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(data.foods_to_limit || []).length > 0 && (
              <Section title="Gently limit" icon={AlertTriangle} testId="lifestyle-limit">
                <div className="flex flex-wrap gap-2">
                  {data.foods_to_limit.map((f, i) => (
                    <Pill key={i}>{f}</Pill>
                  ))}
                </div>
              </Section>
            )}

            {(data.yoga_asanas || []).length > 0 && (
              <Section title="Yoga asanas" icon={Wind} testId="lifestyle-yoga">
                <ul className="space-y-3">
                  {data.yoga_asanas.map((y, i) => (
                    <li key={i} className="text-sm">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium">{y.name}</span>
                        {y.minutes != null && <span className="text-xs text-muted-foreground">{y.minutes} min</span>}
                      </div>
                      <p className="text-muted-foreground">{y.why}</p>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(data.exercises || []).length > 0 && (
              <Section title="Movement" icon={Activity} testId="lifestyle-exercise">
                <ul className="space-y-3">
                  {data.exercises.map((e, i) => (
                    <li key={i} className="text-sm">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium">{e.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {e.minutes != null ? `${e.minutes} min` : ''}
                          {e.frequency ? ` · ${e.frequency}` : ''}
                        </span>
                      </div>
                      <p className="text-muted-foreground">{e.why}</p>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {(data.lifestyle_changes || []).length > 0 && (
            <Section title="Lifestyle shifts" icon={Sparkles} testId="lifestyle-shifts">
              <ul className="grid sm:grid-cols-2 gap-2">
                {data.lifestyle_changes.map((c, i) => (
                  <li key={i} className="text-sm flex gap-2 items-start">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-gold shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(data.evening_winddown || []).length > 0 && (
            <Section title="Evening winddown" icon={Moon} testId="lifestyle-evening">
              <ul className="space-y-1.5">
                {data.evening_winddown.map((c, i) => (
                  <li key={i} className="text-sm flex gap-2 items-start">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-gold shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
